import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMapPin, FiUser, FiArrowRight } from 'react-icons/fi';
import { GiSun, GiMoon } from 'react-icons/gi';
import axios from 'axios';
import DateTimePicker from '../common/DateTimePicker';
import { useHydrated } from '../../hooks/useHydrated';
import { useTranslation } from '../../hooks/useTranslation';
import './Hero.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LANG_ROUTES = {
  en: '/online-horoscope-generator',
  ta: '/jathagam-in-tamil',
};

const Hero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    date: '',
    time: '',
    place: ''
  });
  const [lang, setLang] = useState('en');

  // City search
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const suggestionsRef = useRef(null);
  const hydrated = useHydrated();

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // City autocomplete
  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.place.length >= 2 && !selectedLocation) {
        searchCities(formData.place);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.place, searchCities, selectedLocation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'place') {
      setSelectedLocation(null);
    }
  };

  const handleCitySelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setFormData(prev => ({ ...prev, place: displayName }));
    setSelectedLocation(city);
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate(LANG_ROUTES[lang], { state: { formData, selectedLocation } });
  };

  return (
    <section className="hero">
      <div className="hero-bg-elements">
        <div className="hero-planet planet-1"><GiSun /></div>
        <div className="hero-planet planet-2"><GiMoon /></div>
        <div className="hero-planet planet-3">♄</div>
        <div className="hero-orbit orbit-1"></div>
        <div className="hero-orbit orbit-2"></div>
        <div className="hero-orbit orbit-3"></div>
      </div>

      <div className="container hero-container">
        <motion.div
          className="hero-content"
          initial={hydrated ? { opacity: 0, x: -50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="hero-badge">{t('hero.badge')}</span>
          <h1>
            {t('hero.title')}
          </h1>
          <p>
            {t('hero.subtitle')}
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">2.5L+</span>
              <span className="stat-label">{t('hero.happyUsers')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">12+</span>
              <span className="stat-label">{t('hero.zodiacSignsCount')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">10L+</span>
              <span className="stat-label">{t('hero.horoscopesGenerated')}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hero-form-wrapper"
          initial={hydrated ? { opacity: 0, x: 50 } : false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="hero-form-card">
            <div className="form-header">
              <h3>{t('hero.formTitle')}</h3>
              <p>{t('hero.formSubtitle')}</p>
            </div>
            <form onSubmit={handleSubmit} className="kundli-form">
              <div className="form-group">
                <label htmlFor="name">
                  <FiUser className="input-icon" /> {t('hero.fullName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('hero.enterName')}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="gender">{t('hero.gender')}</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('hero.select')}</option>
                    <option value="male">{t('hero.male')}</option>
                    <option value="female">{t('hero.female')}</option>
                    <option value="other">{t('hero.other')}</option>
                  </select>
                </div>
                <div className="form-group city-search-group" ref={suggestionsRef}>
                  <label htmlFor="place">
                    <FiMapPin className="input-icon" /> {t('hero.birthPlace')}
                  </label>
                  <input
                    type="text"
                    id="place"
                    name="place"
                    value={formData.place}
                    onChange={handleChange}
                    placeholder={t('hero.searchCity')}
                    required
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="city-suggestions">
                      {suggestions.map((city, i) => (
                        <li key={i} onClick={() => handleCitySelect(city)}>
                          {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <DateTimePicker
                date={formData.date}
                time={formData.time}
                onDateChange={(val) => setFormData(prev => ({ ...prev, date: val }))}
                onTimeChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
                dateLabel={t('hero.birthDate')}
                timeLabel={t('hero.birthTime')}
              />

              <div className="form-group form-lang-selector">
                <label>{t('hero.language')}</label>
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                >
                  <option value="en">{t('hero.english')}</option>
                  <option value="ta">{t('hero.tamil')}</option>
                </select>
              </div>

              <button type="submit" className="btn btn-primary btn-full">
                {t('hero.generateHoroscope')} <FiArrowRight />
              </button>
            </form>
            <p className="form-footer">
              {t('hero.dataSecure')}
            </p>
          </div>
        </motion.div>
      </div>

      <div className="hero-scroll-indicator">
        <span>{t('hero.scrollToExplore')}</span>
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
};

export default Hero;
